import { createFileRoute, LoaderFnContext } from '@tanstack/react-router';

import { PageHeader } from '@/components/PageHeader';
import { MainContainer } from '@/components/MainContainer';
import { MyRouterContext } from '@/App';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Box, Group, Skeleton, Stack, Title } from '@mantine/core';
import {
  BillingHistoryBar,
  BillingHistoryCumSumLine,
  TokenHistoryCumSumLine,
} from '@/components/Charts/TokenHistoryBar';
import { MonthSelector } from '@/components/Charts/MonthSelector';
import { transformChartData } from '@/lib/tokenUsage';
import { MonthlySpend, WebSearchUsage } from '@/components/UsageMeter';

export const Route = createFileRoute('/_main/tenant/usage/')({
  loader,
  component: () => <Component />,
});

async function loader({
  context: { adminClient },
}: LoaderFnContext<Record<never, string>, never, MyRouterContext>) {
  const tenant = await adminClient.settings_v1_get_tenant();
  return { tenant, adminClient };
}

function Component() {
  const { tenant, adminClient } = Route.useLoaderData();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const tokenHistoryQuery = useQuery({
    queryKey: ['tenantTokenHistory', { year, month }],
    queryFn: async () => {
      const { items, currency } = await adminClient.admin_v1_get_tenant_token_history({
        queries: {
          year,
          month,
        },
      });

      const { records, models } = transformChartData(year, month, items);

      return {
        items: records,
        currency,
        models,
      };
    },
  });

  return (
    <MainContainer>
      <PageHeader title="利用状況" />
      <Stack>
        <MonthlySpend
          limit={tenant.spec.usage_limits?.limit_monthly_bill}
          currency={tenant.spec.billing_settings.currency}
          queryKey={['tenantMonthlySpend']}
          fetchItems={async (year, month) =>
            (await adminClient.admin_v1_get_tenant_token_history({ queries: { year, month } }))
              .items
          }
        />
        <WebSearchUsage adminClient={adminClient} />
        <MonthSelector year={year} setYear={setYear} month={month} setMonth={setMonth} />
        {tokenHistoryQuery.isLoading && (
          <Skeleton visible w="100%" h="50vh" mt="xl" opacity="30%" />
        )}
        {tokenHistoryQuery.isError && <div>Error</div>}
        {tokenHistoryQuery.data && (
          <>
            <Box mt="xl">
              <Title order={4}>利用額</Title>
              <BillingHistoryBar
                data={tokenHistoryQuery.data.items}
                year={year}
                month={month}
                currency={tokenHistoryQuery.data.currency}
              />
            </Box>
            <Box>
              <Title order={4}>利用額（累積）</Title>
              <BillingHistoryCumSumLine
                data={tokenHistoryQuery.data.items}
                year={year}
                month={month}
                currency={tokenHistoryQuery.data.currency}
              />
            </Box>
            <Box my="lg">
              <Title order={4} my="lg">
                API・モデルごとのトークン使用量
              </Title>
              {tokenHistoryQuery.data.models.map(([service, model]) => (
                <Box key={`${service}-${model}`}>
                  <Title order={6}>
                    {service} （モデル: {model}）
                  </Title>
                  <Group gap="0" justify="space-between">
                    <Box w="50%">
                      <TokenHistoryCumSumLine
                        service={service}
                        model={model}
                        data={tokenHistoryQuery.data.items}
                        inputOrOutput="input_tokens"
                        year={year}
                        month={month}
                      />
                    </Box>
                    <Box w="50%">
                      <TokenHistoryCumSumLine
                        service={service}
                        model={model}
                        data={tokenHistoryQuery.data.items}
                        inputOrOutput="output_tokens"
                        year={year}
                        month={month}
                      />
                    </Box>
                  </Group>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Stack>
    </MainContainer>
  );
}
